import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowLeft, User, Phone, Mail, Scale, Activity, Pill, Home, MapPin, Package, FileText, Search, Trash2, Plus, Minus, Loader, Calendar, X, AlertCircle, CheckCircle } from 'lucide-react';
import ProductSelectionModal from '../components/ProductSelectionModal';
import { ordersApi, leadsApi } from '../services/api';
import { useTheme } from '../context/ThemeContext';

// ─── Reusable Popup ───────────────────────────────────────────────────────────
const Popup = ({ popup, onClose }) => {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';
  if (!popup) return null;
  const isSuccess = popup.type === 'success';
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden border transition-all ${isDark ? 'bg-[#111827] border-white/10' : 'bg-white border-gray-100'}`}>
        <div className={`h-1.5 w-full ${isSuccess ? 'bg-green-500' : 'bg-red-500'}`} />
        <div className="p-6">
          <div className="flex items-start gap-4 mb-5">
            <div className={`flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center ${isSuccess ? 'bg-green-100' : 'bg-red-100'}`}>
              {isSuccess
                ? <CheckCircle className="w-6 h-6 text-green-600" />
                : <AlertCircle className="w-6 h-6 text-red-500" />}
            </div>
            <div className="flex-1 pt-0.5">
              <h3 className={`text-base font-bold ${isSuccess ? 'text-green-700' : 'text-red-700'}`}>
                {popup.title || (isSuccess ? 'Success' : 'Error')}
              </h3>
              <p className={`text-sm mt-1 leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{popup.message}</p>
            </div>
            <button onClick={onClose} className="flex-shrink-0 p-1 rounded-full hover:bg-gray-100 text-gray-400">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex justify-end">
            <button onClick={onClose}
              className={`px-5 py-2 rounded-lg text-sm font-semibold text-white ${isSuccess ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'}`}>
              {isSuccess ? 'Great!' : 'OK, Got it'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

function AddOrder({ onClose }) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';
  const [showSuccess, setShowSuccess] = useState(false);
  const location = useLocation();
  const leadData = location.state?.leadData;
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [leadid, setleadid] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [loadingPincode, setLoadingPincode] = useState(false);
  const [postOfficeOptions, setPostOfficeOptions] = useState([]);

  const [popup, setPopup] = useState(null);
  const showPopup = (type, title, message) => setPopup({ type, title, message });
  const closePopup = () => setPopup(null);

  const [formData, setFormData] = useState({
    name: '',
    contactNumber: '',
    alternateNumber: '',
    email: '',
    gender: 'MALE',
    age: '',
    height: '',
    weight: '',
    actualProblem: '',
    existingTreatment: '',
    maritalStatus: '',
    marriageAnniversary: '',
    addressType: 'City',
    latitude: '',
    longitude: '',
    houseNumber: '',
    streetName: '',
    colonyName: '',
    landmark: '',
    address: '',
    areaPinCode: '',
    city: '',
    postOffice: '',
    district: '',
    state: '',
    orderType: '',
    remarks: '',
    shippingMethod: '',
    paymentType: '',
    paymentMode: '',
    prepaidAmount: 0,
    discount: 0,
    shippingCharges: 0,
  });

  useEffect(() => {
    if (leadData) {
      // ✅ Map orderItems to selectedProducts if coming from a draft
      if (leadData.orderItems && !leadData.selectedProducts) {
        const mappedProducts = leadData.orderItems.map(item => ({
          id: item.productId || item.id,
          name: item.productName || item.name || 'Unknown Product',
          variantName: item.variantName,
          price: item.price || item.unitPrice || 0,
          quantity: item.quantity || 1,
          imageUrl: item.imageUrl || '',
          weight: item.weight || item.size || '',
          category: item.category,
          subCategory: item.subCategory
        }));
        setSelectedProducts(mappedProducts);
      } else if (leadData.selectedProducts) {
        setSelectedProducts(leadData.selectedProducts);
      }

      setFormData({
        name: leadData.customerName || leadData.name || '',
        leadId: leadData.id || '',
        contactNumber: leadData.contactNumber || leadData.phone || leadData.contact || '',
        alternateNumber: leadData.alternateNumber || '',
        source: leadData.source || '',
        campaignName: leadData.campaignName || '',
        orderType: leadData.orderType || 'Fresh Order',
        paymentType: leadData.paymentType || 'COD',
        paymentMode: leadData.paymentMode || 'Cash',
        prepaidAmount: leadData.prepaidAmount || '',
        discount: leadData.discount || '',
        remarks: leadData.remarks || '',
        addressType: leadData.addressType || 'City',
        houseNumber: leadData.houseNumber || '',
        streetName: leadData.streetName || '',
        colonyName: leadData.colonyName || '',
        landmark: leadData.landmark || '',
        areaPinCode: leadData.areaPinCode || leadData.pincode || '',
        city: leadData.city || '',
        postOffice: leadData.postOffice || '',
        district: leadData.district || '',
        state: leadData.state || '',
        prepaidAmount: leadData.prepaidAmount || 0,
        discount: leadData.discount || 0,
        shippingCharges: leadData.shippingCharges || 0,
      });
      setleadid(leadData.id);
      if (leadData.selectedProducts) setSelectedProducts(leadData.selectedProducts);
    }
  }, [leadData]);

  // Handle automatic prepaid amount updates based on payment type
useEffect(() => {
  const grandTotal = calculateGrandTotal();
  if (formData.paymentType === 'Full') {
    setFormData(prev => ({ ...prev, prepaidAmount: grandTotal }));
  } else if (formData.paymentType === 'COD') {
    setFormData(prev => ({ ...prev, prepaidAmount: 0 }));
  }
}, [formData.paymentType, selectedProducts, formData.discount, formData.shippingCharges]);

  const maritalStatuses = ['Married', 'Single'];

  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        if (!value.trim()) return 'Name is required';
        if (value.length < 2) return 'Name must be at least 2 characters';
        if (!/^[a-zA-Z\s]+$/.test(value)) return 'Name should contain only letters';
        return '';
      case 'contactNumber':
        if (!value.trim()) return 'Contact number is required';
        if (!/^[0-9]{10}$/.test(value)) return 'Contact number must be 10 digits';
        if (!/^[6-9]/.test(value)) return 'Must start with 6, 7, 8 or 9';
        return '';
      case 'alternateNumber':
        if (!value.trim()) return 'Alternate number is required';
        if (!/^[0-9]{10}$/.test(value)) return 'Alternate number must be 10 digits';
        if (!/^[6-9]/.test(value)) return 'Must start with 6, 7, 8 or 9';
        if (value && value === formData.contactNumber) return 'Alternate number cannot be same as contact number';
        return '';
      case 'email':
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Invalid email format';
        return '';
      case 'age':
        if (!value) return 'Age is required';
        const numAge = parseInt(value);
        if (isNaN(numAge) || numAge < 1 || numAge > 120) return 'Age must be between 1 and 120';
        return '';
      case 'gender':
        if (!value) return 'Gender is required';
        return '';
      case 'height':
        if (value && (isNaN(value) || value < 1 || value > 300)) return 'Please enter a valid height';
        return '';
      case 'weight':
        if (value && (isNaN(value) || value < 1 || value > 500)) return 'Please enter a valid weight';
        return '';
      case 'areaPinCode':
        if (!value.trim()) return 'Pin code is required';
        if (!/^[0-9]{6}$/.test(value)) return 'Pin code must be 6 digits';
        return '';
      case 'district':
        if (!value.trim()) return 'District is required';
        return '';
      case 'state':
        if (!value.trim()) return 'State is required';
        return '';
      case 'orderType':
        if (!value) return 'Order type is required';
        return '';
      case 'actualProblem':
        if (value && value.length > 500) return 'Problem description too long';
        return '';
      case 'existingTreatment':
        if (value && value.length > 500) return 'Treatment description too long';
        return '';
      case 'maritalStatus':
        return '';
      case 'marriageAnniversary':
        return '';
      default:
        return '';
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Always required fields
    ['name', 'contactNumber', 'gender', 'age', 'areaPinCode', 'district', 'state', 'orderType'].forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) newErrors[field] = error;
    });

    // Address fields validation based on selection
    if (formData.addressType === 'City') {
      if (!formData.city?.trim()) newErrors.city = 'City/Town is required';
      if (!formData.postOffice?.trim()) newErrors.postOffice = 'Post Office is required';
    } else if (formData.addressType === 'Village') {
      if (!formData.colonyName?.trim()) newErrors.colonyName = 'Village Name is required';
      if (!formData.postOffice?.trim()) newErrors.postOffice = 'Post Office is required';
      if (!formData.streetName?.trim()) newErrors.streetName = 'Tehsil/Block is required';
    }
    if (!formData.shippingMethod) newErrors.shippingMethod = 'Shipping Method is required';
    if (!formData.paymentType) newErrors.paymentType = 'Payment type is required';
    if (!formData.paymentMode) newErrors.paymentMode = 'Payment Mode is required';
    // Optional fields validation
    ['alternateNumber', 'email', 'height', 'weight', 'actualProblem', 'existingTreatment'].forEach(field => {
      if (formData[field]) {
        const e = validateField(field, formData[field]);
        if (e) newErrors[field] = e;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBlur = (fieldName) => {
    setTouched({ ...touched, [fieldName]: true });
    setErrors({ ...errors, [fieldName]: validateField(fieldName, formData[fieldName]) });
  };

  const handleChange = (fieldName, value) => {
    // Only allow letters and spaces for name
    if (fieldName === 'name') {
      if (value !== '' && !/^[a-zA-Z\s]*$/.test(value)) return;
    }

    // Prevent non-numeric characters for specific integer fields
    if (['contactNumber', 'alternateNumber', 'age', 'areaPinCode'].includes(fieldName)) {
      if (value !== '' && !/^\d+$/.test(value)) return;
    }

    // For numeric fields with optional decimals
    if (['height', 'weight', 'prepaidAmount', 'discount', 'shippingCharges', 'latitude', 'longitude'].includes(fieldName)) {
      if (value !== '' && !/^-?\d*\.?\d*$/.test(value)) return;
    }

    // Cap Discount and Prepaid Amount such that: (Discount + PrepaidAmount <= Subtotal)
  if (fieldName === 'discount' || fieldName === 'prepaidAmount') {
  const subtotal = calculateSubtotal();
  
  // ✅ Sirf tab cap karo jab products selected hain
  if (subtotal > 0) {
    const numValue = parseFloat(value) || 0;
    const otherField = fieldName === 'discount' ? 'prepaidAmount' : 'discount';
    const otherValue = parseFloat(formData[otherField]) || 0;
    const maxAllowed = Math.max(0, subtotal - otherValue);
    
    if (numValue > maxAllowed) {
      value = maxAllowed.toString();
    }
  }
}

  if (fieldName === 'paymentType') {
  let autoPaymentMode = '';
  let autoPrepaidAmount = 0;

  if (value === 'COD') {
    autoPaymentMode = 'Cash';
    autoPrepaidAmount = 0;
  } else if (value === 'Full') {
    autoPaymentMode = 'Online';
    autoPrepaidAmount = calculateGrandTotal();
  } else if (value === 'Part') {
    autoPaymentMode = 'Online';
    autoPrepaidAmount = 0; // user khud bharega
  }

  setFormData({ 
    ...formData, 
    [fieldName]: value, 
    paymentMode: autoPaymentMode, 
    prepaidAmount: autoPrepaidAmount 
  });
  if (touched[fieldName]) setErrors({ ...errors, [fieldName]: validateField(fieldName, value) });
}else {
      setFormData({ ...formData, [fieldName]: value });
      if (touched[fieldName]) setErrors({ ...errors, [fieldName]: validateField(fieldName, value) });
    }
  };

  // Pincode API integration
const handlePincodeChange = async (pincode) => {
    handleChange('areaPinCode', pincode);

    if (pincode.length === 6 && /^[0-9]{6}$/.test(pincode)) {
        setLoadingPincode(true);
        try {
            const data = await ordersApi.getByPincode(pincode);

            // ── Exact backend response structure ──
            // data = [{Status: "Success", PostOffice: [{Name, District, State, ...}]}]
            const result = Array.isArray(data) ? data[0] : data;

            if (result?.Status !== 'Success') {
                setPostOfficeOptions([]);
                return;
            }

            const postOffices = result?.PostOffice || [];

            // Post office names for dropdown
            setPostOfficeOptions(postOffices.map(po => po.Name));

            // District, State, City from first post office
            if (postOffices.length > 0) {
                const first = postOffices[0];
                setFormData(prev => ({
                    ...prev,
                    city:       first.Block !== 'NA' ? first.Block : first.District || '',
                    postOffice: '',
                    district:   first.District || '',
                    state:      first.State    || '',
                }));
            }

        } catch (error) {
            console.error('Pincode fetch failed:', error);
            setPostOfficeOptions([]);
        } finally {
            setLoadingPincode(false);
        }
    } else {
        setPostOfficeOptions([]);
    }
};

  const handleSelectProduct = (product) => {
    const existing = selectedProducts.find(p => p.id === product.id);
    if (existing) {
      setSelectedProducts(selectedProducts.map(p => p.id === product.id ? { ...p, quantity: p.quantity + 1 } : p));
    } else {
      setSelectedProducts([...selectedProducts, { ...product, quantity: 1 }]);
    }
  };
  const updateQuantity = (productId, change) => {
    setSelectedProducts(
      selectedProducts.map(p => p.id === productId ? { ...p, quantity: Math.max(1, p.quantity + change) } : p)
        .filter(p => p.quantity > 0)
    );
  };
  const removeProduct = (productId) => setSelectedProducts(selectedProducts.filter(p => p.id !== productId));

  const calculateSubtotal = () => selectedProducts.reduce((t, p) => t + p.price * p.quantity, 0);
  const calculateGrossTotal = () => selectedProducts.reduce((t, p) => t + (p.originalPrice || p.price) * p.quantity, 0);
  const calculateProductDiscount = () => selectedProducts.reduce((t, p) => t + ((p.originalPrice || p.price) - p.price) * p.quantity, 0);
  const calculateTotalSaving = () => calculateProductDiscount() + (parseFloat(formData.discount) || 0);

const calculateBalance = () => Math.max(0, 
  calculateGrandTotal() 
  - (parseFloat(formData.prepaidAmount) || 0)
);
  const calculateGrandTotal = () => Math.max(0, 
  calculateSubtotal() 
  - (parseFloat(formData.discount) || 0) 
  + (parseFloat(formData.shippingCharges) || 0)
);

  const orderTypeMap = { 'Fresh Order': 'FRESH_ORDER', 'Repeat Order': 'REPEAT_ORDER' };
  const shippingMethodMap = { 'Free': 'FREE_SHIPPING', 'Standard': 'STANDARD', 'Express': 'EXPRESS' };
  const paymentModeMap = { 'Online': 'ONLINE', 'Cash': 'COD', 'UPI': 'UPI', 'Card': 'CARD', 'COD': 'COD' };
  const paymentTypeMap = { 'Part': 'PART_PAYMENT', 'Full': 'FULL_PAYMENT', 'COD': 'COD' };

  const buildOrderPayload = (overrideLeadId = null) => {
    const paymentMode = paymentModeMap[formData.paymentMode] || 'COD';
    return {
      leadId: formData.leadId,
      customerName: formData.name,
      contactNumber: formData.contactNumber,
      alternateNumber: formData.alternateNumber || null,
      email: formData.email || null,
      gender: formData.gender,
      age: parseInt(formData.age) || 0,
      height: formData.height || null,
      weight: formData.weight || null,
      maritalStatus: formData.maritalStatus || null,
      actualProblem: formData.actualProblem || null,
      existingMedicalTreatment: formData.existingTreatment || null,
      marriageAnniversary: formData.marriageAnniversary || null,
      addressType: formData.addressType || 'City',
      latitude: parseFloat(formData.latitude) || null,
      longitude: parseFloat(formData.longitude) || null,
      houseNumber: formData.houseNumber,
      streetName: formData.streetName || null,
      colonyName: formData.colonyName || null,
      landmark: formData.landmark || null,
      city: formData.city || null,
      cityTown: formData.postOffice || null,
      areaPinCode: formData.areaPinCode,
      district: formData.district,
      state: formData.state,
      orderType: orderTypeMap[formData.orderType] || 'FRESH_ORDER',
      paymentMode,
      paymentType: paymentTypeMap[formData.paymentType] || formData.paymentType || 'COD',
      shippingMethod: shippingMethodMap[formData.shippingMethod] || 'STANDARD',
      remarks: formData.remarks || '',
      discount: parseFloat(formData.discount) || 0,
      shippingCharges: parseFloat((parseFloat(formData.shippingCharges) || 0).toFixed(2)),
      prepaidAmount: parseFloat(formData.prepaidAmount) || 0,
      orderItems: selectedProducts.map(p => ({
        productId: p.id,
        productName: p.name,
        quantity: p.quantity,
        unitPrice: p.price,
        discount: p.discount || 0,
      })),
    };
  };

  const handleSubmit = async () => {
    // Mark all required fields as touched
    const newTouched = {};
    ['name', 'contactNumber', 'gender', 'age', 'orderType', 'areaPinCode', 'district', 'state',
      'shippingMethod', 'paymentType', 'paymentMode'  // ✅ add karein
    ].forEach(f => { newTouched[f] = true; });


    // Mark address fields as touched based on addressType
    if (formData.addressType === 'City') {
      ['city', 'postOffice'].forEach(f => { newTouched[f] = true; });
    } else if (formData.addressType === 'Village') {
      ['colonyName', 'postOffice', 'streetName'].forEach(f => { newTouched[f] = true; });
    }

    setTouched(newTouched);

    if (!validateForm()) {
      const firstErrorField = document.querySelector('.border-red-500');
      if (firstErrorField) firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // Collect ALL error messages including address fields
      const allErrors = { ...errors };
      showPopup('error', 'Please fix the errors', Object.values(allErrors).filter(Boolean).join('\n') || 'Fill all required fields correctly.');
      return;
    }
    if (selectedProducts.length === 0) {
      showPopup('error', 'No products added', 'Please select at least one product to place the order.');
      return;
    }
    const grandTotal = calculateSubtotal() - (parseFloat(formData.discount) || 0) + (parseFloat(formData.shippingCharges) || 0);
    if (grandTotal <= 0) {
      showPopup('error', 'Invalid grand total', 'Grand total cannot be zero or negative.');
      return;
    }
    try {
      setLoading(true);
      const orderPayload = buildOrderPayload();
      const orderResponse = await ordersApi.createOrder(orderPayload);
      console.log('Order created:', orderResponse);
      setShowSuccess(true);
      setTimeout(() => { onClose(); }, 2000);
    } catch (error) {
      console.error('Error creating order:', error);
      showPopup('error', 'Order creation failed', error.error || error.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDraftOrder = async () => {
    try {
      setLoading(true);
      let finalLeadId = !isNaN(Number(leadid)) ? Number(leadid) : null;
      if (!finalLeadId) {
        const minimalLead = {
          customerName: formData.name || 'Draft Customer',
          contactNumber: formData.contactNumber || '0000000000',
          email: formData.email || null,
          gender: formData.gender || 'MALE',
          age: parseInt(formData.age) || 0,
          areaPinCode: formData.areaPinCode || '',
          district: formData.district || '',
          state: formData.state || '',
          status: 'ORDER_PLACED',
          source: 'INSTAGRAM',
        };
        const leadResponse = await leadsApi.createLead(minimalLead);
        finalLeadId = leadResponse?.id || leadResponse?.data?.id;
        if (finalLeadId) setleadid(finalLeadId);
      }
      const draftPayload = buildOrderPayload(finalLeadId);
      const response = await ordersApi.createDraftOrder(draftPayload);
      console.log('Draft saved:', response);
      showPopup('success', 'Draft saved!', 'Your order has been saved as a draft successfully.');
      setTimeout(() => { closePopup(); if (onClose) onClose(); }, 2000);
    } catch (error) {
      console.error('Error saving draft:', error);
      showPopup('error', 'Draft save failed', error.message || 'Failed to save draft order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen p-6 transition-colors duration-500 ${isDark ? 'bg-[#0F172A]' : 'bg-[#F8FAFC]'}`}>
      <Popup popup={popup} onClose={closePopup} />
      <ProductSelectionModal isOpen={showProductModal} onClose={() => setShowProductModal(false)} onSelectProduct={handleSelectProduct} />

      {showSuccess && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50 p-6">
          <div className={`rounded-[40px] p-12 text-center shadow-2xl border transition-all ${isDark ? 'bg-[#111827] border-white/10' : 'bg-white border-gray-100'}`}>
            <div className="w-24 h-24 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className={`text-2xl font-black uppercase tracking-widest ${isDark ? 'text-white' : 'text-gray-800'}`}>Order placed successfully!</h3>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <button onClick={onClose} className={`p-2.5 rounded-xl transition-all shadow-sm ${isDark ? 'bg-white/5 hover:bg-white/10 text-teal-400 border border-white/10' : 'bg-white hover:bg-gray-50 text-[#258F67]'}`}>
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h2 className={`text-2xl desktop:text-3xl font-black uppercase tracking-tight ${isDark ? 'text-white' : 'text-gray-800'}`}>Add New Order</h2>
        </div>
        <button className={`px-6 py-2.5 rounded-xl font-black uppercase tracking-widest text-[10px] transition-all border ${isDark ? 'bg-white/5 text-gray-400 border-white/10 hover:text-white' : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200'}`}>
          Manual Campaign
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 desktop:gap-8">
        {/* Left Column */}
        <div className="col-span-1 lg:col-span-2 space-y-6">

          {/* ── STEP 1 ── */}
          {currentStep === 1 && (
            <>
              {/* Customer Details */}
              <div className={`p-6 rounded-3xl shadow-xl border transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
                <h3 className={`text-lg desktop:text-2xl font-black uppercase tracking-widest mb-6 ${isDark ? 'text-white' : 'text-gray-800'}`}>Customer details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`text-sm font-black uppercase tracking-widest mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>Name <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <svg className="absolute left-1 top-1/2 transform -translate-y-1/2" width="29" height="29" viewBox="0 0 29 29" fill="none">
                        <rect width="29" height="29" rx="4" fill={isDark ? "rgba(255,255,255,0.05)" : "white"} /><circle cx="14.2324" cy="10.3105" r="3.9043" fill="#4CA2FF" />
                        <path d="M20.4781 19.787C20.4781 22.1157 17.6813 22.598 14.2312 22.598C10.7812 22.598 7.98438 22.1157 7.98438 19.787C7.98438 17.4582 10.7812 15.5703 14.2312 15.5703C17.6813 15.5703 20.4781 17.4582 20.4781 19.787Z" fill="#4CA2FF" />
                      </svg>
                      <input type="text" placeholder="Enter name" value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)} onBlur={() => handleBlur('name')}
                        className={`w-full pl-10 pr-4 py-2.5 h-[35px] desktop:h-[45px] border rounded-xl focus:outline-none transition-all ${touched.name && errors.name ? 'border-red-500' : isDark ? 'bg-white/5 border-white/10 text-white focus:border-blue-500/50' : 'bg-gray-50 border-gray-200 focus:ring-1'}`}
                        style={{ borderColor: touched.name && errors.name ? undefined : isDark ? 'transparent' : currentTheme.color }} />
                    </div>
                    {touched.name && errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                  </div>
                  <div>
                    <label className={`text-sm font-black uppercase tracking-widest mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>Contact number <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <svg className="absolute left-1 top-1/2 transform -translate-y-1/2" width="29" height="29" viewBox="0 0 29 29" fill="none">
                        <rect width="29" height="29" rx="4" fill={isDark ? "rgba(255,255,255,0.05)" : "white"} />
                        <path fillRule="evenodd" clipRule="evenodd" d="M9.03279 7.69982C10.1121 6.86579 11.0443 7.01297 11.4858 7.99419C12.3689 9.76038 13.0067 10.6435 12.3689 11.3303C11.1915 12.1153 10.3574 12.5078 10.799 13.3418C11.6821 15.4514 13.3992 17.2667 15.4598 18.346C16.2447 18.8366 16.6863 18.0026 17.5694 16.9233C18.3053 16.3345 19.1393 16.9723 20.8565 18.0026C21.7886 18.5423 21.8377 19.4254 20.9546 20.4557C15.8032 26.2448 2.90018 12.4587 9.03279 7.69982Z" fill="#40C05D" />
                      </svg>
                      <input type="text" placeholder="Enter number" value={formData.contactNumber}
                        onChange={(e) => handleChange('contactNumber', e.target.value)} onBlur={() => handleBlur('contactNumber')}
                        maxLength="10"
                        className={`w-full pl-10 pr-4 py-2.5 h-[35px] desktop:h-[45px] border rounded-xl focus:outline-none transition-all ${touched.contactNumber && errors.contactNumber ? 'border-red-500' : isDark ? 'bg-white/5 border-white/10 text-white focus:border-blue-500/50' : 'bg-gray-50 border-gray-200 focus:ring-1'}`}
                        style={{ borderColor: touched.contactNumber && errors.contactNumber ? undefined : isDark ? 'transparent' : currentTheme.color }} />
                    </div>
                    {touched.contactNumber && errors.contactNumber && <p className="text-red-500 text-xs mt-1">{errors.contactNumber}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className={`text-sm font-black uppercase tracking-widest mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>Alternate number</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-orange-500" />
                      <input type="text" placeholder="Enter number" value={formData.alternateNumber}
                        onChange={(e) => handleChange('alternateNumber', e.target.value)} onBlur={() => handleBlur('alternateNumber')}
                        maxLength="10"
                        className={`w-full pl-10 pr-4 py-2.5 h-[35px] desktop:h-[45px] border rounded-xl focus:outline-none transition-all ${touched.alternateNumber && errors.alternateNumber ? 'border-red-500' : isDark ? 'bg-white/5 border-white/10 text-white focus:border-blue-500/50' : 'bg-gray-50 border-gray-200 focus:ring-1'}`}
                        style={{ borderColor: touched.alternateNumber && errors.alternateNumber ? undefined : isDark ? 'transparent' : currentTheme.color }} />
                    </div>
                    {touched.alternateNumber && errors.alternateNumber && <p className="text-red-500 text-xs mt-1">{errors.alternateNumber}</p>}
                  </div>
                  <div>
                    <label className={`text-sm font-black uppercase tracking-widest mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-yellow-500" />
                      <input type="email" placeholder="Enter email" value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)} onBlur={() => handleBlur('email')}
                        className={`w-full pl-10 h-[35px] desktop:h-[45px] pr-4 py-2.5 border rounded-xl focus:outline-none transition-all ${touched.email && errors.email ? 'border-red-500' : isDark ? 'bg-white/5 border-white/10 text-white focus:border-blue-500/50' : 'bg-gray-50 border-gray-200 focus:ring-1'}`}
                        style={{ borderColor: touched.email && errors.email ? undefined : isDark ? 'transparent' : currentTheme.color }} />
                    </div>
                    {touched.email && errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className={`text-sm font-black uppercase tracking-widest mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>Age <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input type="text" placeholder="Enter age" value={formData.age}
                        onChange={(e) => handleChange('age', e.target.value)} onBlur={() => handleBlur('age')}
                        className={`w-full pl-10 pr-4 h-[35px] desktop:h-[45px] py-2.5 border rounded-xl focus:outline-none transition-all ${touched.age && errors.age ? 'border-red-500' : isDark ? 'bg-white/5 border-white/10 text-white focus:border-blue-500/50' : 'bg-gray-50 border-gray-200 focus:ring-1'}`}
                        style={{ borderColor: touched.age && errors.age ? undefined : isDark ? 'transparent' : currentTheme.color }} />
                    </div>
                    {touched.age && errors.age && <p className="text-red-500 text-xs mt-1">{errors.age}</p>}
                  </div>
                  <div>
                    <label className={`text-sm font-black uppercase tracking-widest mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>Height</label>
                    <div className="relative">
                      <Activity className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-cyan-500" />
                      <input type="text" placeholder="Enter height" value={formData.height}
                        onChange={(e) => handleChange('height', e.target.value)}
                        onBlur={() => handleBlur('height')}
                        className={`w-full pl-10 pr-4 py-2.5 h-[35px] desktop:h-[45px] border rounded-xl focus:outline-none transition-all ${touched.height && errors.height ? 'border-red-500' : isDark ? 'bg-white/5 border-white/10 text-white focus:border-blue-500/50' : 'bg-gray-50 border-gray-200 focus:ring-1'}`}
                        style={{ borderColor: touched.height && errors.height ? undefined : isDark ? 'transparent' : currentTheme.color }} />
                    </div>
                    {touched.height && errors.height && <p className="text-red-500 text-xs mt-1">{errors.height}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className={`text-sm font-black uppercase tracking-widest mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>Gender <span className="text-red-500">*</span></label>
                    <div className="flex gap-2">
                      {['MALE', 'FEMALE'].map(g => (
                        <label key={g} className={`flex items-center space-x-3 px-4 py-2.5 h-[35px] desktop:h-[45px] border rounded-xl cursor-pointer transition-all ${formData.gender === g ? (isDark ? 'border-teal-500 bg-teal-500/10 text-teal-400' : 'border-teal-500 bg-teal-50 text-teal-700') : touched.gender && errors.gender ? 'border-red-500' : isDark ? 'border-white/10 bg-white/5 text-gray-400 hover:border-teal-500/50' : 'border-gray-200 hover:border-teal-500 text-gray-600'}`}>
                          <input type="radio" name="gender" value={g} checked={formData.gender === g}
                            onChange={(e) => handleChange('gender', e.target.value)}
                            onBlur={() => handleBlur('gender')}
                            className="text-teal-600 focus:ring-teal-500" />
                          <span className="text-sm">{g === 'MALE' ? 'Male' : 'Female'}</span>
                        </label>
                      ))}
                    </div>
                    {touched.gender && errors.gender && <p className="text-red-500 text-xs mt-1">{errors.gender}</p>}
                  </div>
                </div>
              </div>

              {/* Basic Details */}
              <div className={`p-6 rounded-3xl shadow-xl border transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
                <h3 className={`text-lg desktop:text-2xl font-black uppercase tracking-widest mb-6 ${isDark ? 'text-white' : 'text-gray-800'}`}>Basic details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`text-sm font-black uppercase tracking-widest mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>Weight</label>
                    <div className="relative">
                      <Scale className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-400" />
                      <input type="text" placeholder="Enter weight" value={formData.weight}
                        onChange={(e) => handleChange('weight', e.target.value)}
                        onBlur={() => handleBlur('weight')}
                        className={`w-full pl-10 pr-4 h-[35px] desktop:h-[45px] py-2.5 border-2 rounded-lg focus:outline-none ${touched.weight && errors.weight ? 'border-red-500' : 'focus:ring-1'}`}
                        style={{ borderColor: touched.weight && errors.weight ? undefined : currentTheme.color, backgroundColor: `${currentTheme.color}10` }} />
                    </div>
                    {touched.weight && errors.weight && <p className="text-red-500 text-xs mt-1">{errors.weight}</p>}
                  </div>
                  <div>
                    <label className={`text-sm font-black uppercase tracking-widest mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>Actual Problem</label>
                    <div className="relative">
                      <Activity className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input type="text" placeholder="Enter actual problem" value={formData.actualProblem}
                        onChange={(e) => handleChange('actualProblem', e.target.value)}
                        onBlur={() => handleBlur('actualProblem')}
                        className={`w-full pl-10 pr-4 py-2.5 h-[35px] desktop:h-[45px] border-2 rounded-lg focus:outline-none ${touched.actualProblem && errors.actualProblem ? 'border-red-500' : 'focus:ring-1'}`}
                        style={{ borderColor: touched.actualProblem && errors.actualProblem ? undefined : currentTheme.color, backgroundColor: `${currentTheme.color}10` }} />
                    </div>
                    {touched.actualProblem && errors.actualProblem && <p className="text-red-500 text-xs mt-1">{errors.actualProblem}</p>}
                  </div>
                  <div>
                    <label className={`text-sm font-black uppercase tracking-widest mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>Existing medical Treatment</label>
                    <div className="relative">
                      <Pill className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-teal-500" />
                      <input type="text" placeholder="Enter existing treatment" value={formData.existingTreatment}
                        onChange={(e) => handleChange('existingTreatment', e.target.value)}
                        onBlur={() => handleBlur('existingTreatment')}
                        className={`w-full pl-10 pr-4 py-2.5 h-[35px] desktop:h-[45px] border-2 rounded-lg focus:outline-none ${touched.existingTreatment && errors.existingTreatment ? 'border-red-500' : 'focus:ring-1'}`}
                        style={{ borderColor: touched.existingTreatment && errors.existingTreatment ? undefined : currentTheme.color, backgroundColor: `${currentTheme.color}10` }} />
                    </div>
                    {touched.existingTreatment && errors.existingTreatment && <p className="text-red-500 text-xs mt-1">{errors.existingTreatment}</p>}
                  </div>
                  <div>
                    <label className={`text-sm font-black uppercase tracking-widest mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>Marital Status</label>
                    <div className="relative">
                      <svg className="absolute left-1 top-1/2 transform -translate-y-1/2 pointer-events-none z-10" width="29" height="29" viewBox="0 0 29 29" fill="none">
                        <rect width="29" height="29" rx="4" fill={isDark ? "rgba(255,255,255,0.05)" : "white"} />
                        <path d="M16.5848 8.32812H12.8828L14.5316 13.8292L16.5848 8.32812Z" fill="#9658FA" />
                        <path d="M20.0156 10.6641C21.4264 12.0735 22.3006 14.0196 22.3008 16.1729C22.3005 20.4817 18.8069 23.9745 14.498 23.9746C10.1891 23.9746 6.69555 20.4817 6.69531 16.1729C6.69531 14.0197 7.56881 12.0735 8.97949 10.6641L10.4785 12.541C9.61006 13.5017 9.08105 14.7756 9.08105 16.1729C9.08129 19.164 11.5069 21.5889 14.498 21.5889C17.4891 21.5887 19.9138 19.1638 19.9141 16.1729C19.9141 14.7756 19.3851 13.5017 18.5166 12.541L20.0156 10.6641Z" fill="#FECF3E" />
                      </svg>
                      <select value={formData.maritalStatus}
                        onChange={(e) => handleChange('maritalStatus', e.target.value)}
                        onBlur={() => handleBlur('maritalStatus')}
                        className={`w-full pl-11 pr-10 py-0 h-[35px] desktop:h-[45px] border-2 rounded-lg focus:outline-none appearance-none text-sm ${touched.maritalStatus && errors.maritalStatus ? 'border-red-500' : ''}`}
                        style={{ borderColor: touched.maritalStatus && errors.maritalStatus ? undefined : currentTheme.color, backgroundColor: `${currentTheme.color}10` }}>
                        <option value="">Please select</option>
                        {maritalStatuses.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    {touched.maritalStatus && errors.maritalStatus && <p className="text-red-500 text-xs mt-1">{errors.maritalStatus}</p>}
                  </div>
                  <div>
                    <label className={`text-sm font-black uppercase tracking-widest mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>Marriage anniversary</label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-pink-400 pointer-events-none" />
                      <input type="date" value={formData.marriageAnniversary}
                        onChange={(e) => handleChange('marriageAnniversary', e.target.value)}
                        onBlur={() => handleBlur('marriageAnniversary')}
                        className={`w-full pl-10 pr-4 py-2.5 h-[35px] desktop:h-[45px] border-2 rounded-lg focus:outline-none text-sm hide-date-icon ${touched.marriageAnniversary && errors.marriageAnniversary ? 'border-red-500' : ''}`}
                        style={{ borderColor: touched.marriageAnniversary && errors.marriageAnniversary ? undefined : currentTheme.color, backgroundColor: `${currentTheme.color}10` }}
                        onClick={(e) => e.target.showPicker && e.target.showPicker()} />
                    </div>
                    {touched.marriageAnniversary && errors.marriageAnniversary && <p className="text-red-500 text-xs mt-1">{errors.marriageAnniversary}</p>}
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button onClick={() => {
                  const step1Fields = ['name', 'contactNumber', 'gender', 'age'];
                  const step1Errors = {};
                  step1Fields.forEach(f => {
                    const err = validateField(f, formData[f]);
                    if (err) step1Errors[f] = err;
                  });

                  if (Object.keys(step1Errors).length > 0) {
                    setErrors(prev => ({ ...prev, ...step1Errors }));
                    const touchedFields = {};
                    step1Fields.forEach(f => touchedFields[f] = true);
                    setTouched(prev => ({ ...prev, ...touchedFields }));
                    showPopup('error', 'Validation Error', 'Please fill all required customer details correctly.');
                  } else {
                    setCurrentStep(2);
                  }
                }}
                  className="flex items-center space-x-2 px-8 py-3 text-white rounded-lg transition-all transform hover:scale-105"
                  style={{ background: currentTheme.color }}>
                  <span>Next</span>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </>
          )}

          {/* ── STEP 2 ── */}
          {currentStep === 2 && (
            <>
              {/* Address */}
              <div className={`p-6 rounded-3xl shadow-xl border transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
                <h3 className={`text-lg desktop:text-2xl font-black uppercase tracking-widest mb-6 ${isDark ? 'text-white' : 'text-gray-800'}`}>Address</h3>

                {/* Map + Lat/Long */}
                <div className="mb-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div className="lg:col-span-2 bg-gray-100 rounded-lg h-[200px] flex items-center justify-center border-2 border-gray-200">
                    <div className="text-center text-gray-500">
                      <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <p className="text-sm">Map View</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {['latitude', 'longitude'].map(key => (
                      <div key={key}>
                        <label className="text-sm text-gray-600 mb-1 block capitalize">{key}</label>
                        <div className="relative">
                          <MapPin className="absolute left-2 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input type="text" placeholder={key === 'latitude' ? '28.613241' : '77.381349'}
                            value={formData[key]} onChange={(e) => handleChange(key, e.target.value)}
                            className="w-full pl-9 pr-4 py-2.5 h-[40px] border-2 bg-gray-50 border-gray-300 rounded-lg focus:outline-none focus:ring-1"
                            style={{ borderColor: currentTheme.color }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Address Type Radio */}
                <div className="mb-4">
                  <label className="text-sm text-gray-600 mb-2 block">Select address type <span className="text-red-500">*</span></label>
                  <div className="flex gap-4">
                    {['City', 'Village'].map(type => (
                      <label key={type}
                        className="flex items-center space-x-2 px-6 py-2.5 h-[40px] border-2 rounded-lg cursor-pointer"
                        style={formData.addressType === type
                          ? { borderColor: currentTheme.color, backgroundColor: `${currentTheme.color}15` }
                          : { borderColor: '#D1D5DB' }}>
                        <input type="radio" name="addressType" value={type} checked={formData.addressType === type}
                          onChange={(e) => {
                            handleChange('addressType', e.target.value);
                            setPostOfficeOptions([]);
                            setFormData(prev => ({ ...prev, postOffice: '', city: '' }));
                          }} className="text-teal-600" />
                        <span className="text-sm font-medium">{type}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* ═══════════════════ CITY FIELDS ═══════════════════ */}
                {formData.addressType === 'City' && (
                  <>
                    {/* Row 1: House, Street, Colony, Landmark */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                      <div>
                        <label className={`text-sm font-black uppercase tracking-widest mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>House /Flat /Building Number</label>
                        <div className="relative">
                          <Home className="absolute left-2 top-1/2 transform -translate-y-1/2 w-5 h-5 text-purple-400" />
                          <input type="text" placeholder="45" value={formData.houseNumber}
                            onChange={(e) => handleChange('houseNumber', e.target.value)}
                            className="w-full pl-9 pr-4 py-2.5 h-[40px] border-2 rounded-lg focus:outline-none focus:ring-1"
                            style={{ borderColor: currentTheme.color, backgroundColor: `${currentTheme.color}10` }} />
                        </div>
                      </div>
                      <div>
                        <label className={`text-sm font-black uppercase tracking-widest mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>Street No./Road Name</label>
                        <div className="relative">
                          <svg className="absolute left-2 top-1/2 transform -translate-y-1/2 w-5 h-5" fill="none" stroke="#f97316" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                          </svg>
                          <input type="text" placeholder="Please enter" value={formData.streetName}
                            onChange={(e) => handleChange('streetName', e.target.value)}
                            className="w-full pl-9 pr-4 py-2.5 h-[40px] border-2 rounded-lg focus:outline-none focus:ring-1"
                            style={{ borderColor: currentTheme.color, backgroundColor: `${currentTheme.color}10` }} />
                        </div>
                      </div>
                      <div>
                        <label className={`text-sm font-black uppercase tracking-widest mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>Colony/Society Name</label>
                        <div className="relative">
                          <svg className="absolute left-2 top-1/2 transform -translate-y-1/2 w-5 h-5" fill="none" stroke="#3b82f6" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                          <input type="text" placeholder="Please enter" value={formData.colonyName}
                            onChange={(e) => handleChange('colonyName', e.target.value)}
                            className="w-full pl-9 pr-4 py-2.5 h-[40px] border-2 rounded-lg focus:outline-none focus:ring-1"
                            style={{ borderColor: currentTheme.color, backgroundColor: `${currentTheme.color}10` }} />
                        </div>
                      </div>
                      <div>
                        <label className={`text-sm font-black uppercase tracking-widest mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>Landmark</label>
                        <div className="relative">
                          <MapPin className="absolute left-2 top-1/2 transform -translate-y-1/2 w-5 h-5 text-orange-400" />
                          <input type="text" placeholder="Please enter" value={formData.landmark}
                            onChange={(e) => handleChange('landmark', e.target.value)}
                            className="w-full pl-9 pr-4 py-2.5 h-[40px] border-2 rounded-lg focus:outline-none focus:ring-1"
                            style={{ borderColor: currentTheme.color, backgroundColor: `${currentTheme.color}10` }} />
                        </div>
                      </div>
                    </div>

                    {/* Row 2: Pin, City/Town, District, State */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <label className={`text-sm font-black uppercase tracking-widest mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>Area pin code <span className="text-red-500">*</span></label>
                        <div className="relative">
                          <MapPin className="absolute left-2 top-1/2 transform -translate-y-1/2 w-5 h-5 text-orange-500" />
                          <input type="text" placeholder="201014" value={formData.areaPinCode}
                            onChange={(e) => handlePincodeChange(e.target.value)} onBlur={() => handleBlur('areaPinCode')}
                            maxLength="6"
                            className={`w-full pl-9 pr-10 py-2.5 h-[40px] border-2 rounded-lg focus:outline-none ${touched.areaPinCode && errors.areaPinCode ? 'border-red-500' : 'focus:ring-1'}`}
                            style={{ borderColor: touched.areaPinCode && errors.areaPinCode ? undefined : currentTheme.color, backgroundColor: `${currentTheme.color}10` }} />
                          {loadingPincode && (
                            <Loader className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-teal-500 animate-spin" />
                          )}
                        </div>
                        {touched.areaPinCode && errors.areaPinCode && <p className="text-red-500 text-xs mt-1">{errors.areaPinCode}</p>}
                      </div>
                      <div>
                        <label className={`text-sm font-black uppercase tracking-widest mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>City/Town <span className="text-red-500">*</span></label>
                        <div className="relative">
                          <svg className="absolute left-2 top-1/2 transform -translate-y-1/2 w-5 h-5" fill="none" stroke="#22c55e" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <input type="text" placeholder="Please enter" value={formData.city || ''}
                            onChange={(e) => handleChange('city', e.target.value)}
                            onBlur={() => handleBlur('city')}
                            className={`w-full pl-9 pr-3 h-[40px] border-2 rounded-lg focus:outline-none text-sm ${touched.city && errors.city ? 'border-red-500' : 'focus:ring-1'}`}
                            style={{ borderColor: touched.city && errors.city ? undefined : currentTheme.color, backgroundColor: `${currentTheme.color}10` }} />
                        </div>
                        {touched.city && errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
                      </div>
                      <div className="mt-4 lg:mt-0 lg:col-span-1">
                        <label className={`text-sm font-black uppercase tracking-widest mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>Post Office <span className="text-red-500">*</span></label>
                        <div className="relative">
                          <MapPin className="absolute left-2 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-500" />
                          {postOfficeOptions.length > 0 ? (
                            <select value={formData.postOffice}
                              onChange={(e) => handleChange('postOffice', e.target.value)}
                              onBlur={() => handleBlur('postOffice')}
                              className={`w-full pl-9 pr-8 h-[40px] border-2 rounded-lg focus:outline-none appearance-none text-sm ${touched.postOffice && errors.postOffice ? 'border-red-500' : 'focus:ring-1'}`}
                              style={{ borderColor: touched.postOffice && errors.postOffice ? undefined : currentTheme.color, backgroundColor: `${currentTheme.color}10` }}>
                              <option value="">Please select</option>
                              {postOfficeOptions.map((loc) => (
                                <option key={loc} value={loc}>{loc}</option>
                              ))}
                            </select>
                          ) : (
                            <input type="text" placeholder="Enter pincode first" value={formData.postOffice}
                              onChange={(e) => handleChange('postOffice', e.target.value)}
                              onBlur={() => handleBlur('postOffice')}
                              className={`w-full pl-9 pr-8 h-[40px] border-2 rounded-lg focus:outline-none text-sm ${touched.postOffice && errors.postOffice ? 'border-red-500' : 'focus:ring-1'}`}
                              style={{ borderColor: touched.postOffice && errors.postOffice ? undefined : currentTheme.color, backgroundColor: `${currentTheme.color}10` }} />
                          )}
                          <svg className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                        {touched.postOffice && errors.postOffice && <p className="text-red-500 text-xs mt-1">{errors.postOffice}</p>}
                      </div>
                      <div>
                        <label className={`text-sm font-black uppercase tracking-widest mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>District <span className="text-red-500">*</span></label>
                        <div className="relative">
                          <svg className="absolute left-2 top-1/2 transform -translate-y-1/2 w-5 h-5" fill="none" stroke="#64748b" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          </svg>
                          <input type="text" placeholder="Please enter" value={formData.district}
                            onChange={(e) => handleChange('district', e.target.value)} onBlur={() => handleBlur('district')}
                            className={`w-full pl-9 pr-4 py-2.5 h-[40px] border-2 rounded-lg focus:outline-none ${touched.district && errors.district ? 'border-red-500' : 'focus:ring-1'}`}
                            style={{ borderColor: touched.district && errors.district ? undefined : currentTheme.color, backgroundColor: `${currentTheme.color}10` }} />
                        </div>
                        {touched.district && errors.district && <p className="text-red-500 text-xs mt-1">{errors.district}</p>}
                      </div>
                      <div>
                        <label className={`text-sm font-black uppercase tracking-widest mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>State <span className="text-red-500">*</span></label>
                        <div className="relative">
                          <svg className="absolute left-2 top-1/2 transform -translate-y-1/2 w-5 h-5" fill="none" stroke="#ef4444" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
                          </svg>
                          <input type="text" placeholder="Please enter" value={formData.state}
                            onChange={(e) => handleChange('state', e.target.value)} onBlur={() => handleBlur('state')}
                            className={`w-full pl-9 pr-4 py-2.5 h-[40px] border-2 rounded-lg focus:outline-none ${touched.state && errors.state ? 'border-red-500' : 'focus:ring-1'}`}
                            style={{ borderColor: touched.state && errors.state ? undefined : currentTheme.color, backgroundColor: `${currentTheme.color}10` }} />
                        </div>
                        {touched.state && errors.state && <p className="text-red-500 text-xs mt-1">{errors.state}</p>}
                      </div>
                    </div>
                  </>
                )}

                {/* ═══════════════════ VILLAGE FIELDS ═══════════════════ */}
                {formData.addressType === 'Village' && (
                  <>
                    {/* Row 1: Ward/House, Village Name, Landmark, Area Pin Code */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                      <div>
                        <label className={`text-sm font-black uppercase tracking-widest mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>Ward Number/House Number/ <span className="text-red-500">*</span></label>
                        <div className="relative">
                          <Home className="absolute left-2 top-1/2 transform -translate-y-1/2 w-5 h-5 text-purple-400" />
                          <input type="text" placeholder="45" value={formData.houseNumber}
                            onChange={(e) => handleChange('houseNumber', e.target.value)}
                            onBlur={() => handleBlur('houseNumber')}
                            className={`w-full pl-9 pr-4 py-2.5 h-[40px] border-2 rounded-lg focus:outline-none ${touched.houseNumber && errors.houseNumber ? 'border-red-500' : 'focus:ring-1'}`}
                            style={{ borderColor: touched.houseNumber && errors.houseNumber ? undefined : currentTheme.color, backgroundColor: `${currentTheme.color}10` }} />
                        </div>
                        {touched.houseNumber && errors.houseNumber && <p className="text-red-500 text-xs mt-1">{errors.houseNumber}</p>}
                      </div>
                      <div>
                        <label className={`text-sm font-black uppercase tracking-widest mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>Village Name <span className="text-red-500">*</span></label>
                        <div className="relative">
                          <svg className="absolute left-2 top-1/2 transform -translate-y-1/2 w-5 h-5" fill="none" stroke="#f97316" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                          </svg>
                          <input type="text" placeholder="Please enter" value={formData.colonyName}
                            onChange={(e) => handleChange('colonyName', e.target.value)}
                            onBlur={() => handleBlur('colonyName')}
                            className={`w-full pl-9 pr-4 py-2.5 h-[40px] border-2 rounded-lg focus:outline-none ${touched.colonyName && errors.colonyName ? 'border-red-500' : 'focus:ring-1'}`}
                            style={{ borderColor: touched.colonyName && errors.colonyName ? undefined : currentTheme.color, backgroundColor: `${currentTheme.color}10` }} />
                        </div>
                        {touched.colonyName && errors.colonyName && <p className="text-red-500 text-xs mt-1">{errors.colonyName}</p>}
                      </div>
                      <div>
                        <label className={`text-sm font-black uppercase tracking-widest mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>Landmark</label>
                        <div className="relative">
                          <MapPin className="absolute left-2 top-1/2 transform -translate-y-1/2 w-5 h-5 text-orange-400" />
                          <input type="text" placeholder="Please enter" value={formData.landmark}
                            onChange={(e) => handleChange('landmark', e.target.value)}
                            className="w-full pl-9 pr-4 py-2.5 h-[40px] border-2 rounded-lg focus:outline-none focus:ring-1"
                            style={{ borderColor: currentTheme.color, backgroundColor: `${currentTheme.color}10` }} />
                        </div>
                      </div>
                      <div>
                        <label className={`text-sm font-black uppercase tracking-widest mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>Area pin code <span className="text-red-500">*</span></label>
                        <div className="relative">
                          <MapPin className="absolute left-2 top-1/2 transform -translate-y-1/2 w-5 h-5 text-orange-500" />
                          <input type="text" placeholder="201014" value={formData.areaPinCode}
                            onChange={(e) => handlePincodeChange(e.target.value)} onBlur={() => handleBlur('areaPinCode')}
                            maxLength="6"
                            className={`w-full pl-9 pr-10 py-2.5 h-[40px] border-2 rounded-lg focus:outline-none ${touched.areaPinCode && errors.areaPinCode ? 'border-red-500' : 'focus:ring-1'}`}
                            style={{ borderColor: touched.areaPinCode && errors.areaPinCode ? undefined : currentTheme.color, backgroundColor: `${currentTheme.color}10` }} />
                          {loadingPincode && (
                            <Loader className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-teal-500 animate-spin" />
                          )}
                        </div>
                        {touched.areaPinCode && errors.areaPinCode && <p className="text-red-500 text-xs mt-1">{errors.areaPinCode}</p>}
                      </div>
                    </div>

                    {/* Row 2: Post Office (dropdown), Tehsil/Taluka/Block, District, State */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <label className={`text-sm font-black uppercase tracking-widest mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>Post Office <span className="text-red-500">*</span></label>
                        <div className="relative">
                          <svg className="absolute left-2 top-1/2 transform -translate-y-1/2 w-5 h-5" fill="none" stroke="#22c55e" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          {postOfficeOptions.length > 0 ? (
                            <select value={formData.postOffice}
                              onChange={(e) => handleChange('postOffice', e.target.value)}
                              onBlur={() => handleBlur('postOffice')}
                              className={`w-full pl-9 pr-8 h-[40px] border-2 rounded-lg focus:outline-none appearance-none text-sm ${touched.postOffice && errors.postOffice ? 'border-red-500' : 'focus:ring-1'}`}
                              style={{ borderColor: touched.postOffice && errors.postOffice ? undefined : currentTheme.color, backgroundColor: `${currentTheme.color}10` }}>
                              <option value="">Please select</option>
                              {postOfficeOptions.map((loc) => (
                                <option key={loc} value={loc}>{loc}</option>
                              ))}
                            </select>
                          ) : (
                            <input type="text" placeholder="Enter pincode first" value={formData.postOffice}
                              onChange={(e) => handleChange('postOffice', e.target.value)}
                              onBlur={() => handleBlur('postOffice')}
                              className={`w-full pl-9 pr-8 h-[40px] border-2 rounded-lg focus:outline-none text-sm ${touched.postOffice && errors.postOffice ? 'border-red-500' : 'focus:ring-1'}`}
                              style={{ borderColor: touched.postOffice && errors.postOffice ? undefined : currentTheme.color, backgroundColor: `${currentTheme.color}10` }} />
                          )}
                          <svg className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                        {touched.postOffice && errors.postOffice && <p className="text-red-500 text-xs mt-1">{errors.postOffice}</p>}
                      </div>
                      <div>
                        <label className={`text-sm font-black uppercase tracking-widest mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>Tehsil/Taluka/Block <span className="text-red-500">*</span></label>
                        <div className="relative">
                          <svg className="absolute left-2 top-1/2 transform -translate-y-1/2 w-5 h-5" fill="none" stroke="#64748b" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          </svg>
                          <input type="text" placeholder="Please enter" value={formData.streetName}
                            onChange={(e) => handleChange('streetName', e.target.value)}
                            onBlur={() => handleBlur('streetName')}
                            className={`w-full pl-9 pr-4 py-2.5 h-[40px] border-2 rounded-lg focus:outline-none ${touched.streetName && errors.streetName ? 'border-red-500' : 'focus:ring-1'}`}
                            style={{ borderColor: touched.streetName && errors.streetName ? undefined : currentTheme.color, backgroundColor: `${currentTheme.color}10` }} />
                        </div>
                        {touched.streetName && errors.streetName && <p className="text-red-500 text-xs mt-1">{errors.streetName}</p>}
                      </div>
                      <div>
                        <label className={`text-sm font-black uppercase tracking-widest mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>District <span className="text-red-500">*</span></label>
                        <div className="relative">
                          <svg className="absolute left-2 top-1/2 transform -translate-y-1/2 w-5 h-5" fill="none" stroke="#64748b" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          </svg>
                          <input type="text" placeholder="Please enter" value={formData.district}
                            onChange={(e) => handleChange('district', e.target.value)} onBlur={() => handleBlur('district')}
                            className={`w-full pl-9 pr-4 py-2.5 h-[40px] border-2 rounded-lg focus:outline-none ${touched.district && errors.district ? 'border-red-500' : 'focus:ring-1'}`}
                            style={{ borderColor: touched.district && errors.district ? undefined : currentTheme.color, backgroundColor: `${currentTheme.color}10` }} />
                        </div>
                        {touched.district && errors.district && <p className="text-red-500 text-xs mt-1">{errors.district}</p>}
                      </div>
                      <div>
                        <label className={`text-sm font-black uppercase tracking-widest mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>State <span className="text-red-500">*</span></label>
                        <div className="relative">
                          <svg className="absolute left-2 top-1/2 transform -translate-y-1/2 w-5 h-5" fill="none" stroke="#ef4444" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
                          </svg>
                          <input type="text" placeholder="Please enter" value={formData.state}
                            onChange={(e) => handleChange('state', e.target.value)} onBlur={() => handleBlur('state')}
                            className={`w-full pl-9 pr-4 py-2.5 h-[40px] border-2 rounded-lg focus:outline-none ${touched.state && errors.state ? 'border-red-500' : 'focus:ring-1'}`}
                            style={{ borderColor: touched.state && errors.state ? undefined : currentTheme.color, backgroundColor: `${currentTheme.color}10` }} />
                        </div>
                        {touched.state && errors.state && <p className="text-red-500 text-xs mt-1">{errors.state}</p>}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Order Details */}
              <div className={`p-6 rounded-3xl shadow-xl border transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
                <h3 className={`text-lg desktop:text-2xl font-black uppercase tracking-widest mb-6 ${isDark ? 'text-white' : 'text-gray-800'}`}>Order details</h3>
                <div className="flex flex-col md:flex-row w-full gap-4">
                  <div>
                    <label className={`text-sm font-black uppercase tracking-widest mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>Order type <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <Package className="absolute left-2 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-500" />
                      <select value={formData.orderType}
                        onChange={(e) => handleChange('orderType', e.target.value)} onBlur={() => handleBlur('orderType')}
                        className="pl-9 w-full md:w-[188px] pr-10 py-0 h-[35px] desktop:h-[45px] border-2 rounded-lg focus:outline-none appearance-none text-sm"
                        style={{ borderColor: currentTheme.color, backgroundColor: `${currentTheme.color}10` }}>
                        <option value="">Please select</option>
                        <option value="Fresh Order">Fresh Order</option>
                        <option value="Repeat Order">Repeat Order</option>
                      </select>
                    </div>
                    {touched.orderType && errors.orderType && <p className="text-red-500 text-xs mt-1">{errors.orderType}</p>}
                  </div>
                  <div className="w-full">
                    <label className={`text-sm font-black uppercase tracking-widest mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>Remarks</label>
                    <input type="text" placeholder="Please type here" value={formData.remarks}
                      onChange={(e) => handleChange('remarks', e.target.value)}
                      className="w-full h-[35px] desktop:h-[45px] pl-3 pr-4 py-2.5 border-2 rounded-lg focus:outline-none focus:ring-1"
                      style={{ borderColor: currentTheme.color, backgroundColor: `${currentTheme.color}10` }} />
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-start">
                <button onClick={() => setCurrentStep(1)}
                  className="px-6 py-2 desktop:px-8 desktop:py-3 bg-gray-500 text-white rounded-lg font-semibold hover:bg-gray-600 transition-colors flex items-center space-x-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  <span>Back</span>
                </button>
              </div>
            </>
          )}
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          <div className={`rounded-3xl shadow-xl border overflow-hidden transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
            <div>
              <div style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : `${currentTheme.color}15` }}>
                <div className="p-5">
                  <h3 className="text-lg lg:text-2xl font-bold mb-4 text-gray-800">Product details</h3>
                  <div className="space-y-4">
                    <label className={`text-sm font-black uppercase tracking-widest mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>Product <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <input type="text" placeholder="Please select" readOnly onClick={() => setShowProductModal(true)}
                        className="w-full pr-10 pl-4 py-2.5 h-[35px] lg:h-[45px] border-2 rounded-lg focus:outline-none cursor-pointer"
                        style={{ borderColor: currentTheme.color, backgroundColor: `${currentTheme.color}10` }} />
                      <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-teal-500 cursor-pointer" onClick={() => setShowProductModal(true)} />
                    </div>
                  </div>

                  {selectedProducts.length > 0 && (
                    <div className="mt-4 space-y-3">
                      {selectedProducts.map((product) => (
                        <div key={product.id} className={`p-3 rounded-2xl border transition-all ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center space-x-3">
                              <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center text-2xl">
                                {product.imageUrl
                                  ? <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover"
                                    onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<span class="text-3xl">📦</span>'; }} />
                                  : <span className="text-3xl">📦</span>}
                              </div>
                              <div>
                                <h4 className="font-semibold text-sm text-gray-800">{product.name}</h4>
                                <p className="text-xs text-gray-500">{product.weight}</p>
                              </div>
                            </div>
                            <button onClick={() => removeProduct(product.id)} className="p-1 hover:bg-red-50 rounded">
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </button>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className={`flex items-center space-x-2 rounded-lg p-1 ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                              <button onClick={() => updateQuantity(product.id, -1)} className="w-6 h-6 flex items-center justify-center hover:bg-white rounded"><Minus className="w-4 h-4" /></button>
                              <span className="w-8 text-center font-semibold">{product.quantity}</span>
                              <button onClick={() => updateQuantity(product.id, 1)} className="w-6 h-6 flex items-center justify-center hover:bg-white rounded"><Plus className="w-4 h-4" /></button>
                            </div>
                            <div className="text-right">
                              <div className="text-sm text-gray-500">Total</div>
                              <div className="text-sm font-bold text-green-600">₹{product.price * product.quantity}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                      <button onClick={() => setShowProductModal(true)}
                        className="w-full py-2 border-2 border-dashed rounded-lg flex items-center justify-center space-x-2"
                        style={{ borderColor: `${currentTheme.color}60`, color: currentTheme.color }}>
                        <Plus className="w-4 h-4" /><span className="text-sm font-medium">Add Item</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-5 space-y-4">
                {[
                  { label: 'Shipping Method', key: 'shippingMethod', options: [{ v: 'Free', l: 'Free Shipping' }, { v: 'Standard', l: 'Standard Shipping' }, { v: 'Express', l: 'Express Shipping' }] },
                  { label: 'Payment type', key: 'paymentType', options: [{ v: 'Part', l: 'Part Payment' }, { v: 'Full', l: 'Full Payment' }, { v: 'COD', l: 'COD' }] },
                  { label: 'Payment Mode', key: 'paymentMode', options: (!formData.paymentType || formData.paymentType === 'Full' || formData.paymentType === 'Part') ? [{ v: 'Online', l: 'Online' }, { v: 'UPI', l: 'UPI' }, { v: 'Card', l: 'Card' }] : [{ v: 'Cash', l: 'Cash' }] },
                ].map(({ label, key, options }) => (
                  <div key={key}>
                    <label className={`text-sm font-black uppercase tracking-widest mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>{label} <span className="text-red-500">*</span></label>
                    <select value={formData[key]} onChange={(e) => handleChange(key, e.target.value)}
                      className="w-full px-4 py-2.5 h-[35px] lg:h-[45px] border-2 rounded-lg focus:outline-none"
                      style={{ borderColor: currentTheme.color, backgroundColor: `${currentTheme.color}10` }}>
                      <option value="">Please select</option>
                      {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                    </select>
                  </div>
                ))}

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                  {['prepaidAmount', 'discount'].map(key => (
                    <div key={key}>
                      <label className="text-sm text-gray-600 mb-1 block capitalize">{key === 'prepaidAmount' ? 'Prepaid amount' : 'Discount'}</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-green-600 font-semibold">₹</span>
                        <input type="text" placeholder="Amount" value={formData[key]}
                          onChange={(e) => handleChange(key, e.target.value)}
                          disabled={key === 'prepaidAmount' && formData.paymentType !== 'Part'}
                          className={`w-full pl-8 pr-4 py-2.5 h-[35px] lg:h-[45px] border-2 rounded-lg focus:outline-none ${key === 'prepaidAmount' && formData.paymentType !== 'Part' ? 'bg-gray-100 cursor-not-allowed opacity-60' : ''}`}
                          style={{ borderColor: currentTheme.color, backgroundColor: key === 'prepaidAmount' && formData.paymentType !== 'Part' ? undefined : `${currentTheme.color}10` }} />
                      </div>
                    </div>
                  ))}
                </div>

                <div>
                  <label className={`text-sm font-black uppercase tracking-widest mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>Order status</label>
                  <select className="w-full px-4 py-2.5 h-[35px] lg:h-[45px] border-2 rounded-lg focus:outline-none" disabled
                    style={{ borderColor: currentTheme.color, backgroundColor: `${currentTheme.color}10` }}>
                    <option>Approval pending</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className={`rounded-3xl shadow-xl border overflow-hidden transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
            <div className="p-5">
              <h3 className={`text-lg desktop:text-2xl font-black uppercase tracking-widest mb-6 ${isDark ? 'text-white' : 'text-gray-800'}`}>Summary</h3>
              <div className="space-y-3">
                <div className="flex justify-between text-sm"><span className="text-gray-600">Sub total (Gross)</span><span className="font-semibold">₹{calculateGrossTotal()}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-600">Total Discounts</span><span className="font-semibold text-green-600">₹{calculateTotalSaving()}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-600">Net Total</span><span className="font-semibold">₹{calculateGrandTotal()}</span></div>
                <div className="flex justify-between text-sm pt-2 border-t border-gray-100"><span className="text-gray-600">Prepaid Amount</span><span className="font-semibold text-green-600">₹{formData.prepaidAmount || 0}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-600">Shipping Charges</span><span className="font-semibold text-emerald-600">Free</span></div>
              </div>
            </div>
            <div className="text-white px-4 py-3 rounded-b-lg flex justify-between items-center" style={{ background: currentTheme.color }}>
              <span className="text-sm font-medium">Balance Amount:</span>
              <span className="text-xl font-bold">₹{calculateBalance()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Buttons */}
      <div className={`border-t p-6 flex justify-end space-x-4 mt-8 ${isDark ? 'border-white/5' : 'border-gray-200'}`}>
        <button onClick={handleDraftOrder} disabled={loading}
          className={`px-8 py-3 desktop:px-10 desktop:py-4 desktop:text-lg rounded-xl font-black uppercase tracking-widest transition-all flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed ${isDark ? 'bg-white/5 text-orange-400 border border-white/10 hover:bg-white/10' : 'bg-orange-100 text-orange-600 hover:bg-orange-200'}`}>
          <FileText className="w-5 h-5" /><span>Draft Order</span>
        </button>
        <button onClick={handleSubmit} disabled={loading}
          className="px-8 py-3 desktop:px-10 desktop:py-4 desktop:text-lg text-white rounded-xl font-black uppercase tracking-widest shadow-xl transition-all flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 active:scale-95"
          style={{ background: currentTheme.color }}>
          {loading
            ? <><Loader className="w-5 h-5 animate-spin" /><span>Processing...</span></>
            : <><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg><span>Place Order</span></>}
        </button>
      </div>
    </div>
  );
}
export default AddOrder;